/**
 * One Metal Market Intelligence article page at
 * `/metal-resources/metal-market-intelligence/<slug>`, rendered by
 * `src/pages/metal-resources/metal-market-intelligence/[slug].tsx` when the slug isn't a
 * category. Its SEO tags and JSON-LD are in `./BlogMetaHead.tsx`, rendered beside it by the page.
 *
 * Content: the hero image with category badges and title, the optional subtitle, and the
 * Contentful rich-text `body` rendered by `components/Shared/RichText/Blog.tsx` →
 * `renderBlogText` (which decides which Contentful node types reach the page). Around it:
 * - links to Ryerson's social media profiles and contact email
 *   (`components/NewsEvents/SocialMedia/SocialMedia.tsx`), not per-article share links;
 * - "Popular Content" (`./PopularContent.tsx`): the newest articles, not a popularity ranking;
 * - the newsletter band (`components/Shared/StayTuned/StayTuned.tsx`), whose GA event value is
 *   `"Blog-Article"` plus the article's category titles as a JSON array;
 * - the "Buying Metal Online" guide PDF link (copy and URL in `./BlogStaticContent.tsx`);
 * - the ready-to-talk lead form (`./SpecialForm.tsx`);
 * - on desktop only, the latest-insights section
 *   (`components/Shared/MetalInsights/MetalInsightsAndResourcesRefactor.tsx`).
 *
 * Desktop and mobile are two copies of the markup under `Media`, switched at the `lg` breakpoint.
 * Everything here renders only in the browser (`components/NextLayouts/RootLayout.tsx` →
 * `HasMounted`), so a missing Contentful field fails in the visitor's browser, not in the build.
 * The newsletter band reads `categories.map` without a guard, so an article with no categories
 * throws on render.
 */
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import { ContentSection, Flex, FlexItem } from "@ryerson/frontend.layout";
import { Badge } from "@ryerson/frontend.notification";
import { Icon } from "@ryerson/frontend.assets";
import { Breadcrumb, Link } from "@ryerson/frontend.navigation";
import { Typography } from "@ryerson/frontend.typography";
import { Media } from "@ryerson/frontend.next";
import { useTheme } from "@ryerson/frontend.theme";
import SocialMedia from "@components/NewsEvents/SocialMedia/SocialMedia";
import styled from "@emotion/styled";
import { renderBlogText } from "@components/Shared/RichText";
import { PopularContent } from "./PopularContent";
import { css } from "@emotion/react";
import StayTunedComponent from "@components/Shared/StayTuned/StayTuned";
import { SpecialForm } from "./SpecialForm";
import Spacer from "@components/Shared/Spacer/Spacer";
import {
	AllContentfulBlogCategory,
	TranslatedAllContentfulBlogResponse,
	TranslatedBlogEntries,
} from "@contentful/blog/types";
import MetalInsightsAndResourcesRefactor from "@components/Shared/MetalInsights/MetalInsightsAndResourcesRefactor";
import { useRouter } from "next/router";

/**
 * The article page's fixed sidebar copy, one language's entry of
 * `./BlogStaticContent.tsx` → `blogStaticeContent`.
 */
export type StaticBlogContent = {
	popularHeading: string;
	ryersonGuideTitle: string;
	/** Also the guide image's `alt` text. */
	ryersonGuideSubTitle: string;
	/** Full URL of the guide PDF on the static assets host. */
	ryersonGuideUrl: string;
};

/** Props of {@link Blog}, built by `[slug].tsx` → `getStaticProps`. */
export type BlogProps = {
	/** The article in all three languages; the visitor's language is picked at render. */
	contentfulBlogContent: TranslatedAllContentfulBlogResponse;
	staticContent: StaticBlogContent;
	/** The six newest articles per language, for Popular Content and the insights section. */
	contentfulBlogEntries: TranslatedBlogEntries;
};

const BreadcrumbContainer = styled.div`
	margin-top: 32px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				margin-top: 0px;
			}
		`;
	}}
`;

const Divider = styled.hr`
	margin-top: 0px;
	margin-bottom: 0px;
	opacity: 0.1;
`;

const Left = styled.div`
	display: inline-block;
	width: calc(100% - 300px);
	vertical-align: top;
	padding: 0;
	box-sizing: border-box;
	height: auto;
	border-radius: 2px;
	margin-top: -270px;
	position: relative;
	margin-bottom: 60px;
`;

const Right = styled.div`
	display: inline-block;
	width: 290px;
	margin-top: -270px;
	vertical-align: top;
	margin-left: 10px;
`;

const Social = styled.div`
	width: 20px;
	height: auto;
	left: -36px;
	position: absolute;
`;

const Hero = styled.div`
	width: 100%;
	height: auto;
	position: relative;
	display: block;
	img {
		width: 100%;
		height: auto;
		display: block;
	}
`;

const FlexContainer = styled.div`
	width: 100%;
	height: 100%;
	display: block;
	position: absolute;
	top: 0;
	padding: 40px 44px;
	z-index: 1;
	box-sizing: border-box;
	left: 0;
`;

const RichContainer = styled.div`
	display: block;
	width: 100%;
	height: auto;
	box-sizing: border-box;
	padding: 50px 0px;
`;

const DownloadSheet = styled.div`
	display: block;
	width: 100%;
	height: auto;
	margin-top: 10px;
	border-radius: 2px;
	overflow: hidden;
`;

const DownloadSheetImage = styled.div`
	display: block;
	height: auto;
	width: 100%;
	position: relative;
	img {
		width: 100%;
		height: auto;
	}
`;

const DownloadLink = styled.div`
	display: block;
	width: 100%;
	height: auto;
	margin-top: 15px;
	padding: 0 3px;
	box-sizing: border-box;
`;

/** The newsletter band's `parent`, the GA event value; renaming it changes analytics reporting. */
const stayTunedParent = "Blog-Article";
/** The article page body; see the file header. */
const Blog: React.FC<BlogProps> = ({
	contentfulBlogContent: contentfulContent,
	staticContent: content,
	contentfulBlogEntries,
}) => {
	const { theme } = useTheme();
	const {
		localization: { language },
	} = useApplication();
	const router = useRouter();

	const [socialReference, setSocialReference] = React.useState<any>(React.createRef());
	const [socialTop, setSocialTop] = React.useState<number>(0);
	// Positions the vertical social-media bar beside the article. The scroll listener is
	// commented out, so this runs once on mount: the bar is placed for the scroll position at
	// load and doesn't follow the reader.
	React.useEffect(() => {
		if (window !== undefined && document !== undefined) {
			//window.addEventListener("scroll", function () {
			if (socialReference.current) {
				let offset = socialReference.current.getBoundingClientRect();
				let startingTop = offset.top - document.body.getBoundingClientRect().top;
				if (
					window.scrollY + 120 >= startingTop &&
					window.scrollY + 265 < socialReference.current.offsetHeight + startingTop
				) {
					setSocialTop(window.scrollY - startingTop + 120);
				} else if (
					socialReference.current.offsetHeight + startingTop <=
					window.scrollY + 265
				) {
					setSocialTop(socialReference.current.offsetHeight - 145);
				} else {
					setSocialTop(0);
				}
			}
			// });
		}
		if (!socialReference) setSocialReference(React.createRef());
	}, []);

	const languageContent = contentfulContent[language];

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<ContentSection type="tertiary" vPadding="20px">
					<BreadcrumbContainer theme={theme}>
						<Breadcrumb type="tertiary" size="xs" />
					</BreadcrumbContainer>

					{/* Nested inside the `lg`-and-up branch, so this `lessThan="lg"` block
					never shows. */}
					<Media lessThan="lg">
						<Spacer
							css={css`
								height: 40px;
							`}
						/>
						<Spacer
							css={css`
								height: 40px;
							`}
						/>
						<SocialMedia orientation="horizontal" type="tertiary" />
					</Media>

					<Spacer
						css={css`
							height: 270px;
						`}
					/>
				</ContentSection>
				<ContentSection type="secondary">
					<Left
						ref={socialReference}
						css={css`
							background-color: ${theme.colors.primary.background};
						`}
					>
						{/* `greaterThan="lg"`: the vertical bar shows only from the breakpoint
						above `lg`. */}
						<Media greaterThan="lg">
							<Social
								css={css`
									top: ${socialTop + "px"};
								`}
							>
								<SocialMedia orientation="vertical" type="tertiary" />
							</Social>
						</Media>
						<Hero>
							<img
								src={languageContent?.heroImage?.file?.url}
								alt={languageContent.seoTitle}
							/>
							<FlexContainer>
								<Flex
									css={css`
										width: 100%;
										height: 100%;
									`}
									justifyContent="space-between"
									direction="column"
								>
									<FlexItem>
										{languageContent.categories &&
											languageContent?.categories.length > 0 &&
											languageContent?.categories.map(
												(category: any, index: number) => {
													return (
														<div
															key={index}
															css={css`
																display: inline-block;
																vertical-align: top;
																margin-bottom: 5px;
																margin-right: 5px;
															`}
														>
															<Badge
																color={category.categoryColor}
																type="primary"
																key={index}
															>
																{category.title}
															</Badge>
														</div>
													);
												}
											)}
									</FlexItem>
									<FlexItem>
										<Typography
											variant="h1"
											css={css`
												max-width: 700px;
												margin-top: 0px;
												letter-spacing: -2px;
												margin-bottom: 0px;
												text-shadow: 0px 0px 4px
													${theme.colors.primary.darkerGray};
											`}
											type="tertiary"
										>
											{languageContent?.title}
										</Typography>
									</FlexItem>
								</Flex>
							</FlexContainer>
						</Hero>
						<RichContainer>
							{languageContent?.subTitle && languageContent.subTitle.length > 0 ? (
								<Typography
									css={css`
										margin-bottom: 56px;
										max-width: 560px;
										letter-spacing: -1px;
										margin-top: 0px;
										margin-left: 40px;
										width: calc(100% - 40px);
										font-size: 28px;
										line-height: 30px;
									`}
									type="primary"
									variant="h2"
								>
									{languageContent.subTitle}
								</Typography>
							) : (
								<></>
							)}
							<Typography
								variant="div"
								type="primary"
								size="md"
								css={css`
									> p {
										margin-bottom: 18px;
										margin-top: 0px;
										max-width: 560px;
										margin-left: 40px;
										width: calc(100% - 40px);
									}
									> ul {
										margin-bottom: 18px;
										margin-top: 0px;
										max-width: 560px;
										margin-left: 40px;
										box-sizing: border-box;
										width: calc(100% - 40px);
									}
									> h2 {
										margin-top: 36px;
										margin-bottom: 42px;
										max-width: 560px;
										margin-left: 40px;
										width: calc(100% - 40px);
									}
									> h3 {
										margin-top: 0px;
										margin-bottom: 4px;
										max-width: 560px;
										margin-left: 40px;
										width: calc(100% - 40px);
									}
									> h4 {
										margin-top: 0px;
										margin-bottom: 4px;
										max-width: 560px;
										margin-left: 40px;
										width: calc(100% - 40px);
									}
									> img {
										max-width: 100%;
										display: block;
										max-width: calc(100% - 40px);
										margin-left: 40px;
									}
								`}
							>
								{renderBlogText(languageContent?.body, "primary", router, theme)}
							</Typography>
						</RichContainer>
					</Left>
					<Right>
						<PopularContent
							title={content.popularHeading}
							excludeSlug={languageContent?.slug}
							blogContent={contentfulBlogEntries}
						/>
						<Spacer
							css={css`
								height: 10px;
							`}
						/>
						<StayTunedComponent
							parent={stayTunedParent}
							parentCategory={JSON.stringify(
								languageContent?.categories.map(
									(category: AllContentfulBlogCategory) => {
										return category.title;
									}
								)
							)}
							slim={true}
						/>
						<DownloadSheet>
							<DownloadSheetImage>
								<img
									src="/images/metal-resources/metal-knowledge/ReadyToBuy.jpg"
									alt={content.ryersonGuideSubTitle}
								/>
								<Typography
									variant="div"
									size="sm"
									color={theme.colors.primary.gray}
									css={css`
										position: absolute;
										left: 24px;
										bottom: 24px;
										width: calc(100% - 48px);
										height: auto;
										z-index: 1;
									`}
								>
									{content.ryersonGuideSubTitle}
								</Typography>
							</DownloadSheetImage>
							<DownloadLink>
								<Link to={content.ryersonGuideUrl}>
									<Icon
										icon="file-pdf"
										size="sm"
										color={theme.colors.primary.link}
										css={css`
											display: inline-block;
											vertical-align: top;
										`}
									/>
									<Typography
										variant="h3"
										color={theme.colors.primary.link}
										css={css`
											font-size: 20px;
											line-height: 25px;
											margin-top: 0px;
											margin-bottom: 0px;
											display: inline-block;
											margin-left: 6px;
											letter-spacing: -1px;
											width: calc(100% - 24px);
											vertical-align: top;
										`}
									>
										{content.ryersonGuideTitle}
									</Typography>
								</Link>
							</DownloadLink>
						</DownloadSheet>
					</Right>
					<Divider
						css={css`
							color: ${theme.colors.primary.darkerGray};
						`}
					/>
				</ContentSection>
				<SpecialForm />
				{/* Desktop only: the mobile branch below has no insights section. */}
				<MetalInsightsAndResourcesRefactor allBlogsData={contentfulBlogEntries} />
			</Media>
			<Media lessThan="lg">
				<ContentSection type="tertiary" vPadding="20px">
					<BreadcrumbContainer theme={theme}>
						<Breadcrumb type="tertiary" size="xs" />
					</BreadcrumbContainer>
					<Spacer
						css={css`
							height: 40px;
						`}
					/>
					<Spacer
						css={css`
							height: 40px;
						`}
					/>
					<Hero>
						<img
							src={languageContent?.heroImage?.file?.url}
							alt={languageContent.seoTitle}
						/>
						<FlexContainer
							css={css`
								padding: 24px 20px;
							`}
						>
							{languageContent?.categories &&
								languageContent?.categories.length > 0 &&
								languageContent?.categories.map((category: any, index: number) => {
									return (
										<div
											key={index}
											css={css`
												display: inline-block;
												vertical-align: top;
												margin-bottom: 5px;
												margin-right: 5px;
											`}
										>
											<Badge
												color={category.categoryColor}
												type="primary"
												key={index}
											>
												{category.title}
											</Badge>
										</div>
									);
								})}
						</FlexContainer>
					</Hero>
					<Typography
						variant="h1"
						css={css`
							margin-top: 24px;
							letter-spacing: -1px;
							margin-bottom: 16px;
						`}
						type="tertiary"
					>
						{languageContent?.title}
					</Typography>
					<Spacer
						css={css`
							height: 40px;
						`}
					/>
					<SocialMedia orientation="horizontal" type="tertiary" />
					<Spacer
						css={css`
							height: 40px;
						`}
					/>
					<Spacer
						css={css`
							height: 40px;
						`}
					/>
				</ContentSection>
				<ContentSection type="primary" vPadding="32px" hPadding="0px">
					{languageContent?.subTitle && languageContent.subTitle.length > 0 ? (
						<Typography
							css={css`
								margin-bottom: 35px;
								letter-spacing: -1px;
								margin-top: 0px;
								font-size: 20px;
								line-height: 24px;
								margin-left: 20px;
								max-width: calc(100% - 40px);
								font-size: 24px !important;
								line-height: 30px !important;
							`}
							type="primary"
							variant="h2"
						>
							{languageContent.subTitle}
						</Typography>
					) : (
						<></>
					)}
					<Typography
						variant="div"
						type="primary"
						size="md"
						css={css`
							> ul {
								margin-bottom: 16px;
								margin-top: 0px;
								margin-left: 20px;
								max-width: calc(100% - 40px);
							}
							> p {
								margin-bottom: 16px;
								margin-top: 0px;
								margin-left: 20px;
								max-width: calc(100% - 40px);
							}
							> h2 {
								margin-top: 32px;
								margin-bottom: 36px;
								margin-left: 20px;
								max-width: calc(100% - 40px);
							}
							> h3 {
								margin-top: 0px;
								margin-bottom: 2px;
								margin-left: 20px;
								max-width: calc(100% - 40px);
							}
							> h4 {
								margin-top: 0px;
								margin-bottom: 2px;
								margin-left: 20px;
								max-width: calc(100% - 40px);
							}
							> img {
								max-width: 100%;
								display: block;
								margin-left: 20px;
								max-width: calc(100% - 40px);
							}
						`}
					>
						{renderBlogText(languageContent?.body, "primary", router, theme)}
					</Typography>

					<ContentSection vPadding="20px" type="primary">
						<SocialMedia
							color={theme.colors.primary.primaryBrand}
							type="primary"
							orientation="horizontal"
						/>
						<Divider
							css={css`
								margin-top: 8px;
								margin-bottom: 56px;
								color: ${theme.colors.primary.darkerGray};
							`}
						/>
						<PopularContent
							title={content.popularHeading}
							excludeSlug={languageContent?.slug}
							blogContent={contentfulBlogEntries}
						/>
						<Spacer
							css={css`
								height: 10px;
							`}
						/>
					</ContentSection>
					<StayTunedComponent
						parent={stayTunedParent}
						parentCategory={JSON.stringify(
							languageContent?.categories.map(
								(category: AllContentfulBlogCategory) => {
									return category.title;
								}
							)
						)}
					/>
					<ContentSection vPadding="20px" type="primary">
						<DownloadSheet>
							<DownloadSheetImage>
								<img
									src="/images/metal-resources/metal-knowledge/ReadyToBuy.jpg"
									alt={content.ryersonGuideSubTitle}
								/>
								<Typography
									variant="div"
									size="sm"
									color={theme.colors.primary.gray}
									css={css`
										position: absolute;
										left: 24px;
										bottom: 24px;
										width: calc(100% - 48px);
										height: auto;
										z-index: 1;
									`}
								>
									{content.ryersonGuideSubTitle}
								</Typography>
							</DownloadSheetImage>
							<DownloadLink>
								<Link to={content.ryersonGuideUrl}>
									<Icon
										icon="file-pdf"
										size="sm"
										color={theme.colors.primary.link}
										css={css`
											display: inline-block;
											vertical-align: top;
										`}
									/>
									<Typography
										variant="div"
										size="lg"
										weight="bold"
										color={theme.colors.primary.link}
										css={css`
											margin-top: 0px;
											margin-bottom: 0px;
											display: inline-block;
											margin-left: 6px;
											width: calc(100% - 24px);
											vertical-align: top;
										`}
									>
										{content.ryersonGuideTitle}
									</Typography>
								</Link>
							</DownloadLink>
						</DownloadSheet>
					</ContentSection>
				</ContentSection>
				<SpecialForm />
			</Media>
		</>
	);
};

export default Blog;
